import { get as emberGet, set as emberSet, getProperties as emberGetProperties } from '@ember/object';
import { expandProperties } from '@ember/object/computed';

import { eq } from 'rise-ui/utils/choice.js';

export function get(obj, prop) {
  return emberGet(obj, String(prop)?.replaceAll('@', 'args.'));
}

export function set(obj, prop, value, tolerant = undefined) {
  return emberSet(obj, String(prop)?.replaceAll('@', 'args.'), value, tolerant);
}

/**
 * Excludes null and array
 */
export function isObj(value) {
  return value && !Array.isArray(value) && typeof value === 'object';
}

/**
 * Excludes null
 */
export function isObject(value) {
  return value && typeof value === 'object';
}

export function getAndSetWithDefault(root, key, defaultValue) {
  let value = get(root, key);
  if (value === undefined) {
    value = defaultValue;
    set(root, key, value);
  }
  return value;
}

export function softTest(obj, key, value, direct = false) {
  return !eq(direct ? obj?.[key] : get(obj, key), value);
}

export function softTestProperties(obj, properties) {
  for (const key in properties) {
    if (softTest(obj, key, properties[key])) {
      return true;
    }
  }
  return false;
}

/**
 * Sets value for key on object if not already softly-equal
 * @param {*} obj Object on which to assign property
 * @param {string} key Key on object to set value for
 * @param {*} value Value to set if changed
 * @param {boolean} direct True to avoid get/set
 * @returns {boolean} Whether the value was changed/set
 */
export function softSet(obj, key, value, direct = false) {
  if (!obj) {
    return false;
  }
  const changed = softTest(obj, key, value, direct);
  if (changed) {
    if (direct) {
      obj[key] = value;
    } else {
      set(obj, key, value);
    }
  }
  return changed;
}

export function softSetProperties(obj, properties, direct = false) {
  let result = false;
  for (const key in properties) {
    if (softSet(obj, key, properties[key], direct) && !result) {
      result = true;
    }
  }
  return result;
}

export function pick(obj, props) {
  const result = {};
  (props || []).forEach(key => {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      result[key] = obj[key];
    }
  });
  return result;
}

export function truthTable(keyArray) {
  const table = {};
  [].concat(keyArray || []).forEach(key => table[key] = true);
  return table;
}

export function getProperties(obj, ...paths) {
  const props = [];
  paths.forEach(path => {
    expandProperties(path, prop => props.push(prop));
  });
  return emberGetProperties(obj, props);
}

export function filter(object, predicate) {
  const result = {};
  Object.entries(object || {}).forEach(([ key, value ], i, array) => {
    if (predicate([ key, value ], i, array, object)) {
      result[key] = value;
    }
  });
  return result;
}

export function isChanged(object, key, value) {
  return !eq(object?.[key], value);
}

export function setIfChanged(target, key, value, targetKeysOnly = false) {
  if (!target || targetKeysOnly && !target.hasOwnProperty(key)) {
    return false;
  }
  const changed = isChanged(target, key, value);
  if (changed) {
    target[key] = value;
  }
  return changed;
}

export function assignIfChanged(target, source, targetKeysOnly = false) {
  let result = false;
  for (const key in source) {
    if (setIfChanged(target, key, source[key], targetKeysOnly) && !result) {
      result = true;
    }
  }
  return result;
}

/**
 * Filter and map source entries into target object.
 * Return array from callback with new key and value, or boolean value to
 * include/exclude given entry.
 * @param {{}} target
 * @param {{}} source
 * @param {*|function([key:*,value:*],i:number,source:{},target:{}):(boolean|[key:*,value:*])} valueOrCallback
 * @returns {{}}
 */
export function filterMapEntries(target, source, valueOrCallback) {
  const isFn = typeof valueOrCallback === 'function';
  let i = -1;
  for (const key in source) {
    const v = !isFn ? [ key, valueOrCallback ]
      : valueOrCallback([ key, source[key] ], ++i, source, target);
    if (!v) {
      continue;
    }
    if (Array.isArray(v)) {
      target[v[0]] = v[1];
    } else {
      target[key] = source[key];
    }
  }
  return target;
}

/**
 * Map source entries into target object.
 * Return array from callback with new key and value, or non-array value will be
 * applied to given key.
 * @param {{}} target
 * @param {{}} source
 * @param {*|function([key:*,value:*],i:number,source:{},target:{}):(*|[key:*,value:*])} valueOrCallback
 * @returns {{}}
 */
export function mapEntries(target, source, valueOrCallback) {
  const isFn = typeof valueOrCallback === 'function';
  return filterMapEntries(target, source, ([ key, value ], i) => {
    value = isFn ? valueOrCallback([ key, value ], i, source, target) : valueOrCallback;
    return Array.isArray(value) ? value : [ key, value ];
  });
}

export const inverseTuple = ([ k, v ]) => [ v, k ];

/**
 * Inverse source entries (key/values) into target object
 * @param target
 * @param source
 * @returns {{}}
 */
export function inverseEntries(target, source) {
  return mapEntries(target, source, inverseTuple);
}

/**
 * Map source values into target object.
 * Return value from callback will be applied to given key.
 * @param {{}} target
 * @param {{}} source
 * @param {*|function(value:*,key:*,source:{},target:{}):*} valueOrCallback
 * @returns {{}}
 */
export function mapValues(target, source, valueOrCallback) {
  const isFn = typeof valueOrCallback === 'function';
  return mapEntries(target, source, ([ key, v ]) => {
    return [ key, isFn ? valueOrCallback(v, key, source, target) : valueOrCallback ];
  });
}
